import { SubmissionError } from 'redux-form';
import { replace } from 'react-router-redux';
import get from 'lodash/get';
import moment from 'moment';

import forEach from 'lodash/forEach';
import pick from 'lodash/pick';
import difference from 'lodash/difference';
import isNull from 'lodash/isNull';
import flatten from 'lodash/flatten';

import http from '../../../../shared/http';
import endpoints from '../../../../../config/endpoints';
import {
	createSelectOptionsFromSysEnums,
	createSelectValuesUseIntersection
} from '../../../../shared/form-elements/select';
import { actions as parentActions } from '../../../editor';

const isRocket = process.env.CPA_ENV === 'rocket';

const defaultInitialState = {
	currentLanguage: 'en',
	fileToUpload: null,
	fields: {
		title: '',
		domain: '',
		niche: null,
		category: null,
		category_id: null,
		tags: null,
		status: null,
		preview_domain: null,
		past_approve_leads_per_day: null,
		rules: '',
		is_exclusive: false,
		is_test: false,
		is_new: false,
		is_top_offer: false,
		access_on_request: false,
		is_on_home_page_ru: false,
		is_on_home_page_en: false,
		external_integration: '',
		use_redirect: false,
		display_priority: 0,
		subtitle: '',
		leadhub_offer_id: null,
		priority: null,
		crm_id: null,
		analog_ids: null,
		additional_crm_id: null,
		additional_crm_offer_country_id: null,
		sale_model: '',
		external_offer_payment_type: '',
		alternative_titles: '',
		date_of_last_recall_campaign: null,
		comment: ''
	},
	mustTranslateFields: {
		title: '',
		rules: '',
		subtitle: ''
	}
};

const createGeneralForm = ({
	namespace,
	stateNodeName
}) => {
	// action types
	const MAIN_FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-success`;
	const MAIN_FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-error`;
	const MAIN_FORM_CHANGE_LANGUAGE = `${namespace}/${stateNodeName}-change-language`;
	const MAIN_FORM_TRANSLATION_REQUEST_SUCCESS = `${namespace}/${stateNodeName}-translation-request-success`;
	const MAIN_FORM_TRANSLATION_REQUEST_ERROR = `${namespace}/${stateNodeName}-translation-request-error`;

	const MAIN_FORM_UPLOAD_SUCCESS = `${namespace}/${stateNodeName}-upload-success`;
	const MAIN_FROM_UPLOAD_ERROR = `${namespace}/${stateNodeName}-upload-error`;
	const MAIN_FORM_PREPARATION_FILE = `${namespace}/${stateNodeName}-preparation-file-success`;

	const MAIN_FORM_SUGGEST_SUCCESS = `${namespace}/${stateNodeName}-fetch-suggest-success`;
	const LOOKUP_FETCH_SUCCESS = `${namespace}/${stateNodeName}-LOOKUP_FETCH_SUCCESS`;
	const MAIN_FORM_SUGGEST_ERROR = `${namespace}/${stateNodeName}-fetch-suggest-error`;

	// initState
	const initialState = {
		...defaultInitialState
	};

	// actions
	const actions = {
		mainFormSubmit: (formData) => (dispatch, getState) => {
			const { mainForm, data } = getState().programsEditor;
			const url = data.id ? `${endpoints.OFFERS.BASE}/${data.id}` : endpoints.OFFERS.BASE;
			const method = data.id ? 'put' : 'post';
			const maxPayoutFields = selectors.getPayoutCurrencies(getState()).map(i => `max_payout_${i.value}`);

			if (formData.alternative_titles && formData.alternative_titles.length) {
				formData.alternative_titles = flatten(formData.alternative_titles.map(val => val.split(',')));
			}

			const request = http({
				url,
				method,
				actions: [
					MAIN_FORM_SUBMIT_SUCCESS,
					MAIN_FORM_SUBMIT_ERROR
				],
				data: pick({
					...mainForm.fields,
					...formData,
					date_of_last_recall_campaign: formData.date_of_last_recall_campaign
						? formData.date_of_last_recall_campaign.format('YYYY-MM-DD')
						: null,
					category: formData.category && formData.category[0] && formData.category[0].value
						|| (isRocket ? 'other' : null),
					category_id: get(formData, 'category_id.0.value', null),
					niche: isRocket ? null : get(formData, 'niche.0.value', null),
					priority: get(formData, 'priority.0.value', null),
					status: formData.status && formData.status[0] && formData.status[0].value || null,
					analog_ids: get(formData, 'analog_ids.0.value')
						? formData.analog_ids.map(item => item.value)
						: null,
					sale_model: formData.sale_model && formData.sale_model[0] && formData.sale_model[0].value || null,
					external_offer_payment_type: get(formData, 'external_offer_payment_type.0.value', null),
					external_integration: get(formData, 'external_integration.0.value', null),
					use_redirect: Boolean(get(formData, 'external_integration.0.value')) && formData.use_redirect,
					leadhub_offer_id: get(formData, 'external_integration.0.value') ? null : formData.leadhub_offer_id,
					crm_id: get(formData, 'external_integration.0.value') || !formData.crm_id ? null : formData.crm_id,
					additional_crm_id: !formData.additional_crm_id ? null : formData.additional_crm_id,
					additional_crm_offer_country_id:
						get(formData, 'additional_crm_offer_country_id.0.value', null),
					alternative_titles: formData.alternative_titles
						? formData.alternative_titles.map(val => val.trim()).filter(val => val)
						: null
				}, [...Object.keys(mainForm.fields), ...maxPayoutFields])
			});

			return request(dispatch).then(action => {
				if (action.type === MAIN_FORM_SUBMIT_SUCCESS) {
					const { fileToUpload } = getState().programsEditor.mainForm;

					// @todo Adhock for redux-form submit functionality.
					setTimeout(() => dispatch(replace(`/programs/editor/${action.payload.data.id}`)), 200);

					if (fileToUpload) {
						actions.uploadFile(action.payload.data.id, fileToUpload, dispatch);
					} else {
						dispatch(parentActions.toggleProgressBar());
						dispatch(parentActions.fetchOfferData(action.payload.data.id));
					}
				}
				if (action.type === MAIN_FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					throw new SubmissionError(prepareFormErrors(responseErrors.errors || responseErrors));
				}
			});
		},

		mainFormChangeLanguage: lang => (dispatch, getState) => {
			const { defaultLanguage, offer } = getState().programsEditor;
			const actionChangeLang = {type: MAIN_FORM_CHANGE_LANGUAGE, payload: lang};
			const request = actions.translationRequest(offer.id, lang);

			if (lang === defaultLanguage) return dispatch(actionChangeLang);

			dispatch(parentActions.toggleProgressBar());

			dispatch(request).then(action => {
				if (action.type === MAIN_FORM_TRANSLATION_REQUEST_SUCCESS) {
					dispatch({
						...actionChangeLang,
						meta: action.payload.data
					});
				}
			});
		},

		mainFormSubmitTranslation: formData => (dispatch, getState) => {
			const { mainForm: { mustTranslateFields, currentLanguage }, offer } = getState().programsEditor;

			const fields = pick({
				...mustTranslateFields,
				...formData
			}, Object.keys(mustTranslateFields));

			const request = actions.translationRequest(offer.id, currentLanguage, 'put', fields);

			return dispatch(request).then(action => {
				if (action.type === MAIN_FORM_TRANSLATION_REQUEST_SUCCESS) {
					dispatch(actions.mainFormChangeLanguage(currentLanguage));
				}

				if (action.type === MAIN_FORM_TRANSLATION_REQUEST_ERROR && action.payload.response.status === 400) {
					const responseErrors = action.payload.response.data;
					throw new SubmissionError(prepareFormErrors(responseErrors));
				}
			});
		},
		locationsLookup: text => (dispatch, getState) => {

			const request = http({
				url: endpoints.LOOKUPS.LOCATIONS,
				method: 'get',
				actions: ['CATEGORIES_LOOKUP_SUCCESS'],
				query: { query: text }
			});

			return request(dispatch).then(action => {
				if (action.type === 'CATEGORIES_LOOKUP_SUCCESS') {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		analogOffersLookup: text => (dispatch, getState) => {

			const request = http({
				url: endpoints.LOOKUPS.OFFERS,
				method: 'get',
				actions: [ LOOKUP_FETCH_SUCCESS ],
				query: { query: text }
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_FETCH_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		translationRequest: (id, lang, method = 'get', data = {}) => dispatch => {
			const url = endpoints.OFFERS.BASE_TRANSLATION.replace('{id}', id).replace('{lang}', lang);
			return (http({
				url,
				method,
				actions: [
					MAIN_FORM_TRANSLATION_REQUEST_SUCCESS,
					MAIN_FORM_TRANSLATION_REQUEST_ERROR
				],
				data
			}))(dispatch);
		},

		preparationFile: (file, nextAction) => dispatch => {
			const reader = new FileReader();
			const data = new FormData();

			data.append('image', file);

			reader.readAsDataURL(file);
			reader.onloadend = function () {
				nextAction(reader.result);
				dispatch({
					type: MAIN_FORM_PREPARATION_FILE,
					payload: data
				});
			};
		},

		uploadFile: (offerId, fileToUpload, dispatch) => {
			const request = http({
				url: endpoints.OFFERS.UPLOAD_OFFER_IMAGE.replace('{id}', offerId),
				method: 'put',
				actions: [
					MAIN_FORM_UPLOAD_SUCCESS,
					MAIN_FROM_UPLOAD_ERROR
				],
				data: fileToUpload
			});

			dispatch(parentActions.toggleProgressBar());

			request(dispatch).then(action => {
				if (action.type === MAIN_FORM_UPLOAD_SUCCESS) {
					dispatch(parentActions.fetchOfferData(action.payload.data.id));
				}
			});
		},

		categoriesLookupsRP: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS_CATEGORIES_RP}?query=${text}`,
				method: 'get',
				actions: [MAIN_FORM_SUGGEST_SUCCESS, MAIN_FORM_SUGGEST_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === MAIN_FORM_SUGGEST_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(action.payload.data)
					};
				}
			});
		}
	};

	// reducers
	const reducers = {
		[MAIN_FORM_CHANGE_LANGUAGE]: (state, action) => {
			const { payload, meta } = action;

			return {
				...state,
				showProgressBar: false,
				mainForm: {
					...state.mainForm,
					currentLanguage: payload,
					mustTranslateFields: Object.assign(
						{},
						state.mainForm.mustTranslateFields,
						meta ? meta: initialState.mustTranslateFields
					)
				}
			};
		},
		[MAIN_FORM_SUBMIT_ERROR]: errorReducerFn,
		[MAIN_FORM_TRANSLATION_REQUEST_ERROR]: errorReducerFn,
		[MAIN_FROM_UPLOAD_ERROR]: errorReducerFn,
		[MAIN_FORM_PREPARATION_FILE]: (state, action) => ({
			...state,
			mainForm: {
				...state.mainForm,
				fileToUpload: action.payload
			}
		})
	};

	// selectors
	const selectors = {
		getMainFormData: state => {
			const { offer, mainForm: { mustTranslateFields, currentLanguage  }, defaultLanguage } = state.programsEditor;
			const {
				category,
				niche,
				category_rp,
				status,
				priority,
				sale_model,
				external_integration,
				external_offer_payment_type,
				additional_crm_offer_country_name,
				additional_crm_offer_country_id,
				date_of_last_recall_campaign
			} = !isNull(offer) && offer || {};
			const categoriesSelectOptions = createSelectOptionsFromSysEnums(state.root.lists['offer-categories']);
			const nicheSelectOptions = createSelectOptionsFromSysEnums(state.root.lists['offer-niches']);
			const statusesSelectOptions = createSelectOptionsFromSysEnums(state.root.lists['offer-statuses']);
			const saleModelsSelectOptions = createSelectOptionsFromSysEnums(state.root.lists['offer-sale-models']);
			const payoutCurrencies = selectors.getPayoutCurrencies(state);
			const externalOfferPaymentType = createSelectOptionsFromSysEnums(
				state.root.lists['external-offer-payment-type']
			);
			const offerPrioritiesEnum = createSelectOptionsFromSysEnums(
				state.root.lists['offer-priorities']
			);

			const externalIntegrationsOptions = createSelectOptionsFromSysEnums(
				state.root.lists['offer-external-integrations']
			);
			const shouldTranslate = defaultLanguage != currentLanguage;
			const translateMixinFields = shouldTranslate ? mustTranslateFields : {};
			const disabledFields = [];
			if (shouldTranslate) {
				disabledFields.push(difference(
					Object.keys(state.programsEditor.data),
					Object.keys(mustTranslateFields)
				));
			}
			if (!get(state, 'form.programsMainForm.values.external_integration.0.value')) {
				disabledFields.push('use_redirect');
			} else {
				disabledFields.push('leadhub_offer_id');
			}
			return {
				shouldTranslate,
				selectOptions: {
					categories: categoriesSelectOptions,
					niche: nicheSelectOptions,
					statuses: statusesSelectOptions,
					saleModels: saleModelsSelectOptions,
					externalOfferPaymentType: externalOfferPaymentType,
					offerPrioritiesEnum: offerPrioritiesEnum,
					externalIntegrations: externalIntegrationsOptions
				},
				disabledFields,
				initialValues: {
					...state.programsEditor.data,
					...translateMixinFields,
					date_of_last_recall_campaign: date_of_last_recall_campaign ? moment(date_of_last_recall_campaign) : null,
					category: createSelectValuesUseIntersection(categoriesSelectOptions, [category]),
					niche: createSelectValuesUseIntersection(nicheSelectOptions, [niche]),
					category_id: category_rp && [{
						value: category_rp.id,
						label: category_rp.name_en
					}],
					additional_crm_offer_country_id: additional_crm_offer_country_id && [{
						value: additional_crm_offer_country_id,
						label: additional_crm_offer_country_name
					}],
					external_offer_payment_type: createSelectValuesUseIntersection(
						externalOfferPaymentType, [external_offer_payment_type]
					),
					...(isNull(offer)
						? payoutCurrencies.reduce((acc, { value }) => {acc[`max_payout_${value}`] = 50; return acc;}, {})
						: {}
					),
					status: createSelectValuesUseIntersection(statusesSelectOptions, [status]),
					priority: createSelectValuesUseIntersection(offerPrioritiesEnum, [priority]),
					sale_model: createSelectValuesUseIntersection(saleModelsSelectOptions, [sale_model]),
					alternative_titles: state.programsEditor.data && state.programsEditor.data.alternative_titles || '',
					external_integration: createSelectValuesUseIntersection(externalIntegrationsOptions, [external_integration])
				}
			};
		},
		getPayoutCurrencies: state => createSelectOptionsFromSysEnums(state.root.lists['payout-currencies'])
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

// functions
const prepareFormErrors = errors => {
	const validationsErrors = {};
	forEach(errors, value => {
		const key = value.dataPath.slice(1);
		validationsErrors[key] = key === 'category_id' ? 'incorrect value' : value.message;
	});
	return validationsErrors;
};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return ({ ...state, error: true, showProgressBar: false });
	}
	return state;
};

export default createGeneralForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/components/forms/general/index.js